import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Header } from '../components/shell/Header'
import {
  listTeam,
  createEmployee,
  updateEmployee,
  deleteEmployee,
  inviteEmployee,
  revokeEmployeeAccess,
  getSettings,
  updateDisciplines,
  type ManagedEmployee,
} from '../lib/api'
import { refreshRoster } from '../lib/roster'
import { useSession } from '../hooks/useAuth'
import { roleCanManage, ROLES, ROLE_LABEL, ROLE_RANK, type Role } from '@shared/enums'
import { Plus, X, Upload, UserPlus, Mail, Shield, Tag, Trash2, Send, KeyRound, Pencil } from 'lucide-react'
import { TeamImport } from '../components/team/TeamImport'

const RANDOM_COLORS = [
  '#00D1FF', '#3FB68B', '#E0A93B', '#E5484D', '#A855F7',
  '#F97316', '#06B6D4', '#8B5CF6', '#EC4899', '#14B8A6',
]

function randomColor(): string {
  return RANDOM_COLORS[Math.floor(Math.random() * RANDOM_COLORS.length)] ?? '#00D1FF'
}

function deriveInitials(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0] ?? '')
    .join('')
    .toUpperCase()
}

/**
 * Login state is derived from the auth tables (`hasLogin`/`hasPassword`),
 * not from the stored `inviteStatus` string — the roster row and the login are
 * separate records, and only the auth tables know whether someone can actually
 * sign in.
 */
type LoginState = 'active' | 'pending' | 'roster_only'

function loginState(emp: ManagedEmployee): LoginState {
  if (emp.hasPassword) return 'active'
  if (emp.hasLogin) return 'pending'
  return 'roster_only'
}

const LOGIN_STATE_LABEL: Record<LoginState, string> = {
  active: 'Can sign in',
  pending: 'Invited',
  roster_only: 'No login',
}

export function Team() {
  const { data: session } = useSession()
  const sessionUser = session?.user as { role?: string; employeeId?: string } | undefined
  const actorRole = sessionUser?.role ?? 'member'
  const canManage = roleCanManage(actorRole)
  const [showAdd, setShowAdd] = useState(false)
  const [showImport, setShowImport] = useState(false)

  const { data, isLoading } = useQuery({
    queryKey: ['team'],
    queryFn: () => listTeam(),
  })

  const employees = data?.items ?? []

  // Group: can sign in first, then invited, then roster-only
  const sortOrder: Record<LoginState, number> = { active: 0, pending: 1, roster_only: 2 }
  const sorted = [...employees].sort((a, b) => sortOrder[loginState(a)] - sortOrder[loginState(b)])
  const withLogin = employees.filter((e) => e.hasLogin).length

  return (
    <div className="page">
      <Header title="Team" />

      <div className="team-toolbar">
        <p className="text-muted" style={{ margin: 0, fontSize: 13 }}>
          {employees.length} team member{employees.length !== 1 ? 's' : ''}
          {' · '}
          {withLogin} with a login
          {' · '}
          {employees.length - withLogin} roster-only
        </p>
        {canManage && (
          <div style={{ display: 'flex', gap: 'var(--space-1)' }}>
            <button className="btn btn-ghost btn-sm" onClick={() => setShowImport(true)}>
              <Upload size={15} /> Import
            </button>
            <button className="btn btn-primary btn-sm" onClick={() => setShowAdd(true)}>
              <UserPlus size={15} /> Add team member
            </button>
          </div>
        )}
      </div>

      {showAdd && canManage && (
        <AddTeamMember actorRole={actorRole} onClose={() => setShowAdd(false)} />
      )}

      {showImport && canManage && (
        <TeamImport onClose={() => setShowImport(false)} />
      )}

      {isLoading ? (
        <p className="text-muted" style={{ padding: 'var(--space-2)' }}>Loading…</p>
      ) : (
        <div className="team-list">
          <div className="team-list__header">
            <span>Name</span>
            <span>Email</span>
            <span>Role</span>
            <span>Login</span>
            <span>Disciplines</span>
            {canManage && <span />}
          </div>
          {sorted.map((emp) => (
            <TeamRow
              key={emp.id}
              employee={emp}
              canManage={canManage}
              actorRole={actorRole}
              isSelf={emp.id === sessionUser?.employeeId}
            />
          ))}
        </div>
      )}

      {/* Disciplines config — admin only */}
      {canManage && <DisciplinesSection />}
    </div>
  )
}

function TeamRow({
  employee,
  canManage,
  actorRole,
  isSelf,
}: {
  employee: ManagedEmployee
  canManage: boolean
  actorRole: string
  isSelf: boolean
}) {
  const qc = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [confirmRevoke, setConfirmRevoke] = useState(false)

  const state = loginState(employee)
  const isRosterOnly = state === 'roster_only'
  const isPending = state === 'pending'

  // Mirrors the server's rank guard so we don't offer actions that will 403.
  const rank = (r: string) => ROLE_RANK[r as Role] ?? -1
  const canEdit = canManage && rank(actorRole) >= rank(employee.role)

  const refresh = () => refreshRoster(qc)
  const onError = (err: any) =>
    setError(err?.body?.error?.message || err?.message || 'Something went wrong')

  const invite = useMutation({
    mutationFn: () => inviteEmployee(employee.id),
    onSuccess: () => { setError(null); refresh() },
    onError,
  })
  const revoke = useMutation({
    mutationFn: () => revokeEmployeeAccess(employee.id),
    onSuccess: () => { setError(null); setConfirmRevoke(false); refresh() },
    onError,
  })
  const remove = useMutation({
    mutationFn: () => deleteEmployee(employee.id),
    onSuccess: () => { setError(null); refresh() },
    onError,
  })

  const busy = invite.isPending || revoke.isPending || remove.isPending

  return (
    <>
      <div className={`team-row${isRosterOnly ? ' team-row--roster' : ''}${isPending ? ' team-row--pending' : ''}`}>
        <div className="team-row__name">
          <span
            className="assignee-chip__avatar"
            style={{
              background: `${employee.avatarColor}22`,
              color: employee.avatarColor,
              opacity: isRosterOnly ? 0.7 : 1,
            }}
          >
            {employee.initials}
          </span>
          <div className="team-row__identity">
            <span className="team-row__display-name">
              {employee.name}
              {!employee.active && <span className="text-muted text-xs"> · inactive</span>}
            </span>
            {employee.title && <span className="team-row__title text-muted text-xs">{employee.title}</span>}
          </div>
        </div>
        <span className="team-row__email text-muted">
          {employee.email}
          <a href={`mailto:${employee.email}`} className="team-row__mail-link" title="Send email">
            <Mail size={12} />
          </a>
        </span>
        <span className="team-row__role">
          <Shield size={12} className="text-muted" />
          {ROLE_LABEL[employee.role as Role] ?? employee.role}
        </span>
        <span className="team-row__status">
          <span className={`invite-badge invite-badge--${state}`}>{LOGIN_STATE_LABEL[state]}</span>
        </span>
        <span className="team-row__disciplines">
          {(employee.disciplines as string[]).length > 0
            ? (employee.disciplines as string[]).map((d) => (
                <span key={d} className="chip chip-sm">{d}</span>
              ))
            : <span className="text-muted text-xs">—</span>
          }
        </span>

        {canManage && (
          <span className="team-row__actions">
            {canEdit && (
              <button
                className="btn btn-ghost btn-xs"
                onClick={() => { setError(null); setEditing(true) }}
                title="Edit team member"
              >
                <Pencil size={12} /> Edit
              </button>
            )}

            {/* Grant a login, or re-send the set-password link if it expired. */}
            {canEdit && employee.active && !employee.hasPassword && (
              <button
                className="btn btn-ghost btn-xs"
                onClick={() => invite.mutate()}
                disabled={busy}
                title={employee.hasLogin ? 'Re-send the set-password link' : 'Create a login and email a set-password link'}
              >
                {employee.hasLogin ? <Send size={12} /> : <KeyRound size={12} />}
                {invite.isPending ? 'Sending…' : employee.hasLogin ? 'Resend invite' : 'Grant login'}
              </button>
            )}

            {canEdit && employee.hasLogin && !isSelf && (
              <button
                className="btn btn-ghost btn-xs text-danger"
                onClick={() => setConfirmRevoke(true)}
                disabled={busy}
                title="Delete their login — the roster entry and history stay"
              >
                Revoke
              </button>
            )}

            {/* Deleting is only offered once there's no login to strand. */}
            {canEdit && !employee.hasLogin && !isSelf && (
              <button
                className="btn btn-ghost btn-xs text-danger"
                onClick={() => {
                  if (confirm(`Remove ${employee.name} from the team roster?`)) remove.mutate()
                }}
                disabled={busy}
                title="Remove from roster"
              >
                <Trash2 size={12} />
              </button>
            )}
          </span>
        )}
      </div>

      {error && (
        <div className="team-row__error text-danger text-xs">
          {error}
          <button className="btn-icon" onClick={() => setError(null)} aria-label="Dismiss">
            <X size={12} />
          </button>
        </div>
      )}

      {confirmRevoke && (
        <ConfirmRevoke
          employee={employee}
          pending={revoke.isPending}
          onConfirm={() => revoke.mutate()}
          onCancel={() => setConfirmRevoke(false)}
        />
      )}

      {editing && (
        <EditTeamMember
          employee={employee}
          actorRole={actorRole}
          isSelf={isSelf}
          onClose={() => setEditing(false)}
        />
      )}
    </>
  )
}

function ConfirmRevoke({
  employee,
  pending,
  onConfirm,
  onCancel,
}: {
  employee: ManagedEmployee
  pending: boolean
  onConfirm: () => void
  onCancel: () => void
}) {
  return (
    <div className="quick-add-backdrop" onClick={onCancel}>
      <div className="quick-add" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 420 }}>
        <div className="quick-add-header">
          <h3>Revoke access</h3>
          <button onClick={onCancel} className="btn-icon" aria-label="Close">
            <X size={18} />
          </button>
        </div>
        <div className="team-add-form">
          <p style={{ margin: 0, fontSize: 13 }}>
            <strong>{employee.name}</strong> will be signed out immediately and won't be able to log
            in again until re-invited.
          </p>
          <p className="text-muted text-xs" style={{ margin: 0 }}>
            Their roster entry, assignments and history stay intact — they just lose the login.
          </p>
          <div className="team-add-form__actions">
            <button className="btn btn-danger btn-sm" onClick={onConfirm} disabled={pending}>
              {pending ? 'Revoking…' : 'Revoke access'}
            </button>
            <button className="btn btn-ghost btn-sm" onClick={onCancel}>Cancel</button>
          </div>
        </div>
      </div>
    </div>
  )
}

function EditTeamMember({
  employee,
  actorRole,
  isSelf,
  onClose,
}: {
  employee: ManagedEmployee
  actorRole: string
  isSelf: boolean
  onClose: () => void
}) {
  const qc = useQueryClient()
  const [name, setName] = useState(employee.name)
  const [email, setEmail] = useState(employee.email)
  const [title, setTitle] = useState(employee.title ?? '')
  const [initials, setInitials] = useState(employee.initials)
  const [role, setRole] = useState(employee.role)
  const [active, setActive] = useState(employee.active)
  const [error, setError] = useState<string | null>(null)

  const mutation = useMutation({
    mutationFn: () =>
      updateEmployee(employee.id, {
        name: name.trim(),
        email: email.trim().toLowerCase(),
        initials: initials.trim().toUpperCase(),
        title: title.trim() || undefined,
        role,
        active,
      }),
    onSuccess: () => {
      refreshRoster(qc)
      onClose()
    },
    onError: (err: any) => setError(err?.body?.error?.message || err.message || 'Failed to save'),
  })

  return (
    <div className="quick-add-backdrop" onClick={onClose}>
      <div className="quick-add" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480 }}>
        <div className="quick-add-header">
          <h3>Edit {employee.name}</h3>
          <button onClick={onClose} className="btn-icon" aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <form
          className="team-add-form"
          onSubmit={(e) => { e.preventDefault(); setError(null); mutation.mutate() }}
        >
          <div className="team-add-form__row">
            <div className="team-add-form__field">
              <label>Name *</label>
              <input value={name} onChange={(e) => setName(e.target.value)} required autoFocus />
            </div>
            <div className="team-add-form__field">
              <label>Email *</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
            </div>
          </div>

          <div className="team-add-form__row">
            <div className="team-add-form__field">
              <label>Title</label>
              <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Marketing Lead" />
            </div>
            <div className="team-add-form__field">
              <label>Initials *</label>
              <input value={initials} onChange={(e) => setInitials(e.target.value)} maxLength={4} required />
            </div>
          </div>

          <div className="team-add-form__row">
            <div className="team-add-form__field">
              <label>Platform role</label>
              <RoleSelect
                value={role}
                actorRole={actorRole}
                disabled={isSelf}
                onChange={setRole}
              />
              {isSelf && <span className="text-muted text-xs">You can't change your own role.</span>}
            </div>
            <div className="team-add-form__field">
              <label>Roster status</label>
              <select
                value={active ? 'active' : 'inactive'}
                onChange={(e) => setActive(e.target.value === 'active')}
                disabled={isSelf}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive — blocked from signing in</option>
              </select>
            </div>
          </div>

          {employee.hasLogin && email.trim().toLowerCase() !== employee.email && (
            <p className="text-muted text-xs" style={{ margin: 0 }}>
              Changing the email also changes the address they sign in with.
            </p>
          )}

          {error && <p className="text-danger text-xs">{error}</p>}

          <div className="team-add-form__actions">
            <button type="submit" className="btn btn-primary btn-sm" disabled={mutation.isPending}>
              {mutation.isPending ? 'Saving…' : 'Save changes'}
            </button>
            <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
          </div>
        </form>
      </div>
    </div>
  )
}

/** Only offers roles at or below the actor's own rank — the API enforces the same. */
function RoleSelect({
  value,
  actorRole,
  disabled,
  onChange,
}: {
  value: string
  actorRole: string
  disabled?: boolean
  onChange: (role: string) => void
}) {
  const actorRank = ROLE_RANK[actorRole as Role] ?? -1
  const options = ROLES.filter((r) => ROLE_RANK[r] <= actorRank)

  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled}>
      {options.map((r) => (
        <option key={r} value={r}>{ROLE_LABEL[r]}</option>
      ))}
      {/* Keep the current value selectable even if it outranks the actor. */}
      {!options.includes(value as Role) && (
        <option value={value}>{ROLE_LABEL[value as Role] ?? value}</option>
      )}
    </select>
  )
}

function AddTeamMember({ actorRole, onClose }: { actorRole: string; onClose: () => void }) {
  const qc = useQueryClient()
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [title, setTitle] = useState('')
  const [inviteStatus, setInviteStatus] = useState<'pending' | 'roster_only'>('roster_only')
  const [role, setRole] = useState('member')
  const [error, setError] = useState<string | null>(null)

  const mutation = useMutation({
    mutationFn: () => {
      const initials = deriveInitials(name)
      return createEmployee({
        name: name.trim(),
        email: email.trim().toLowerCase(),
        initials,
        avatarColor: randomColor(),
        title: title.trim() || undefined,
        role,
        inviteStatus,
      })
    },
    onSuccess: (created) => {
      refreshRoster(qc)
      // The roster row saved but the invite email didn't go out — say so
      // rather than closing on what looks like a clean success.
      if (created.inviteError) {
        setError(`Added to the roster, but the invite failed: ${created.inviteError}`)
        return
      }
      onClose()
    },
    onError: (err: any) => {
      setError(err?.body?.error?.message || err.message || 'Failed to add team member')
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    if (!name.trim()) return
    if (!email.trim()) return
    mutation.mutate()
  }

  return (
    <div className="quick-add-backdrop" onClick={onClose}>
      <div className="quick-add" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480 }}>
        <div className="quick-add-header">
          <h3>Add team member</h3>
          <button onClick={onClose} className="btn-icon" aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="team-add-form">
          <div className="team-add-form__row">
            <div className="team-add-form__field">
              <label>Name *</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Sarah Chen"
                required
                autoFocus
              />
            </div>
            <div className="team-add-form__field">
              <label>Email *</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="sarah@company.com"
                required
              />
            </div>
          </div>

          <div className="team-add-form__row">
            <div className="team-add-form__field">
              <label>Title</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Marketing Lead"
              />
            </div>
            <div className="team-add-form__field">
              <label>Platform role</label>
              <RoleSelect value={role} actorRole={actorRole} onChange={setRole} />
            </div>
          </div>

          <div className="team-add-form__field">
            <label>Login</label>
            <div className="team-add-form__radio-group">
              <label className="team-add-form__radio">
                <input
                  type="radio"
                  name="inviteStatus"
                  value="roster_only"
                  checked={inviteStatus === 'roster_only'}
                  onChange={() => setInviteStatus('roster_only')}
                />
                <span>
                  <strong>Roster only</strong>
                  <span className="text-muted text-xs"> — Can be assigned to work, no login needed yet</span>
                </span>
              </label>
              <label className="team-add-form__radio">
                <input
                  type="radio"
                  name="inviteStatus"
                  value="pending"
                  checked={inviteStatus === 'pending'}
                  onChange={() => setInviteStatus('pending')}
                />
                <span>
                  <strong>Invite now</strong>
                  <span className="text-muted text-xs"> — Email them a link to set a password</span>
                </span>
              </label>
            </div>
            <span className="text-muted text-xs">
              You can grant a login later from the team list either way.
            </span>
          </div>

          {error && <p className="text-danger text-xs">{error}</p>}

          <div className="team-add-form__actions">
            <button type="submit" className="btn btn-primary btn-sm" disabled={mutation.isPending || !name.trim() || !email.trim()}>
              <Plus size={14} /> {mutation.isPending ? 'Adding…' : 'Add member'}
            </button>
            <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}


function DisciplinesSection() {
  const qc = useQueryClient()
  const [newDiscipline, setNewDiscipline] = useState('')

  const { data, isLoading } = useQuery({
    queryKey: ['settings'],
    queryFn: getSettings,
  })

  const disciplines = (data?.disciplines as string[]) ?? ['sales', 'marketing', 'ops']

  const mutation = useMutation({
    mutationFn: (updated: string[]) => updateDisciplines(updated),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['settings'] }),
  })

  const handleAdd = () => {
    const val = newDiscipline.trim().toLowerCase()
    if (!val || disciplines.includes(val)) return
    mutation.mutate([...disciplines, val])
    setNewDiscipline('')
  }

  const handleRemove = (d: string) => {
    const updated = disciplines.filter((x) => x !== d)
    if (updated.length === 0) return // must keep at least one
    mutation.mutate(updated)
  }

  const handleRename = (old: string, next: string) => {
    const val = next.trim().toLowerCase()
    if (!val || (val !== old && disciplines.includes(val))) return
    mutation.mutate(disciplines.map((d) => (d === old ? val : d)))
  }

  return (
    <section className="disciplines-section">
      <div className="disciplines-section__header">
        <Tag size={16} className="text-accent" />
        <h2>Disciplines</h2>
        <span className="text-muted text-xs">Categories for organizing work</span>
      </div>

      {isLoading ? (
        <p className="text-muted text-xs">Loading…</p>
      ) : (
        <div className="disciplines-list">
          {disciplines.map((d) => (
            <DisciplineChip
              key={d}
              value={d}
              onRename={(next) => handleRename(d, next)}
              onRemove={() => handleRemove(d)}
              canRemove={disciplines.length > 1}
            />
          ))}
        </div>
      )}

      <div className="disciplines-add">
        <input
          type="text"
          value={newDiscipline}
          onChange={(e) => setNewDiscipline(e.target.value)}
          placeholder="Add discipline…"
          className="task-detail-input"
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); handleAdd() }
          }}
        />
        <button
          className="btn btn-primary btn-xs"
          onClick={handleAdd}
          disabled={!newDiscipline.trim() || mutation.isPending}
        >
          <Plus size={12} /> Add
        </button>
      </div>
    </section>
  )
}

function DisciplineChip({
  value,
  onRename,
  onRemove,
  canRemove,
}: {
  value: string
  onRename: (next: string) => void
  onRemove: () => void
  canRemove: boolean
}) {
  const [editing, setEditing] = useState(false)
  const [editValue, setEditValue] = useState(value)

  if (editing) {
    return (
      <span className="discipline-chip discipline-chip--editing">
        <input
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={() => { onRename(editValue); setEditing(false) }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { onRename(editValue); setEditing(false) }
            if (e.key === 'Escape') { setEditValue(value); setEditing(false) }
          }}
          className="discipline-chip__input"
          autoFocus
        />
      </span>
    )
  }

  return (
    <span className="discipline-chip">
      <span
        className="discipline-chip__label"
        onClick={() => { setEditValue(value); setEditing(true) }}
        title="Click to rename"
      >
        {value}
      </span>
      {canRemove && (
        <button
          className="discipline-chip__remove"
          onClick={onRemove}
          aria-label={`Remove ${value}`}
        >
          <Trash2 size={11} />
        </button>
      )}
    </span>
  )
}
